import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingBag } from 'lucide-react'
import { CardContent, CardIcon } from '@/components/ui/card'
import { AppShell } from '@/components/AppShell'
import { BulkActionBar } from '@/components/BulkActionBar'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { ListPagination } from '@/components/ListPagination'
import { PageSizeSelect } from '@/components/PageSizeSelect'
import { RetailConversionTagsDialog } from '@/components/RetailConversionTagsDialog'
import { RetailCreativesDialog } from '@/components/RetailCreativesDialog'
import { RetailProductFilters } from '@/components/RetailProductFilters'
import { RetailProductTable } from '@/components/RetailProductTable'
import { SearchInput } from '@/components/SearchInput'
import { useRetailProductList } from '@/hooks/useRetailProductList'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import type { RetailProduct } from '@/services/productFeed/productFeedApi'

/** The products in the network's retail feeds, to advertise or to track sales of. */
export function RetailProductsPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useUrlSearch()
  const list = useRetailProductList(search)
  const [creating, setCreating] = useState(false)
  const [tagsFor, setTagsFor] = useState<RetailProduct | null>(null)

  return (
    <AppShell crumbs={[{ label: t('retailProducts.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('retailProducts.title')}</h1>
            <p>{t('retailProducts.subtitle')}</p>
          </div>
        </div>

        <ListCard tone="creative">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={ShoppingBag} />
                {t('retailProducts.productCount', { count: list.totalCount })}
              </>
            }
            actions={<PageSizeSelect value={list.pageSize} onChange={list.setPageSize} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => list.refilter(() => setSearch(v))} />
                <RetailProductFilters
                  filters={list.filters}
                  onChange={list.setFilters}
                  facets={list.facets}
                  loading={list.facetsLoading}
                />
              </>
            }
          />

          {list.selection.length > 0 && (
            <BulkActionBar
              count={list.selection.length}
              actions={[
                {
                  label: t('retailProducts.createCreatives', { count: list.selection.length }),
                  icon: 'plus',
                  onClick: () => setCreating(true),
                },
              ]}
              onClear={list.clearSelection}
            />
          )}

          <CardContent className="p-0">
            <RetailProductTable
              products={list.products}
              loading={list.loading}
              merchants={list.merchants}
              selection={list.selection}
              onSelectionChange={list.changeSelection}
              onConversionTags={setTagsFor}
              columnsKey="retailProducts"
            />
          </CardContent>

          <ListPagination
            page={list.page}
            pageSize={list.pageSize}
            totalCount={list.totalCount}
            onPageChange={list.setPage}
            summary={t('retailProducts.productCount', { count: list.totalCount })}
          />
        </ListCard>
      </div>

      <RetailCreativesDialog
        open={creating}
        onOpenChange={setCreating}
        products={list.selection}
        merchants={list.merchants}
        onCreated={list.clearSelection}
      />
      <RetailConversionTagsDialog product={tagsFor} onClose={() => setTagsFor(null)} />
    </AppShell>
  )
}
